import api from "../api";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridColumnMenu,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import {
  Alert,
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import Papa from 'papaparse';

function CustomColumnMenu(props) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsItem: null,
      }}
    />
  );
}
function calculateBilanQualiStatus(salon) {
  const currentDate = new Date();
  const salonEndDate = new Date(salon.EndDate);

  if (!salonEndDate || isNaN(salonEndDate)) {
    return "Date de fin invalide";
  }

  if (currentDate < salonEndDate) {
    return "Sans Objet";
  }

  const bilanQualiFields = [
    "LocationQuality__c",
    "LeadQuality__c",
    "TrafficQuality__c",
    "QualityOfTheReception__c",
    "Observations__c",
    "Improvments__c",
    "ToRedo__c",
  ];

  if (salon.ToRedo__c === "Oui") {
    bilanQualiFields.push("DesiredArea__c", "DesiredLocation__c");
  }

  const emptyFields = bilanQualiFields.filter(
    (field) => salon[field] === null || salon[field] === undefined || salon[field] === ""
  );

  if (emptyFields.length === 0) {
    return "Complet";
  }

  if (
    currentDate >= salonEndDate &&
    emptyFields.length === bilanQualiFields.length
  ) {
    return "Non saisi";
  }

  if (currentDate >= salonEndDate && emptyFields.length > 0) {
    return "Partiel";
  }
}

// Couleur de pastille par statut, dans l'ordre d'affichage des filtres.
const STATUS_COLORS = {
  "Non saisi": "error",
  "Partiel": "warning",
  "Complet": "success",
  "Sans Objet": "default",
};

const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  const date = new Date(year, month - 1, day);
  return !isNaN(date) ? date.toLocaleDateString() : 'Date invalide';
};

// Trois colonnes N / N-1 / N-2 regroupées sous un même en-tête.
const yearColumns = (fields) =>
  fields.map((field, i) => ({
    field,
    headerName: ["N", "N-1", "N-2"][i],
    headerClassName: "app-theme",
    type: "number",
    width: 72,
  }));

const QUANTI_GROUPS = [
  { groupId: "Prospects", fields: ["ProspectsMetN__c", "ProspectsMetN1__c", "ProspectsMetN2__c"] },
  { groupId: "Candidats potentiels", fields: ["PotentielCandidateN__c", "PotentialCandidateN1__c", "PotentialCandidateN2__c"] },
  { groupId: "Candidats", fields: ["DCN__c", "DCN1__c", "DCN2__c"] },
  { groupId: "Inscrits", fields: ["RegisteredN__c", "RegisteredN1__c", "RegisteredN2__c"] },
];

const propertyMapping = {
  'Organizer': 'Organisateur',
  'School__c': 'Ecole',
  'ToRedo__c': 'A refaire',
  'LocationQuality__c': 'Qualité de l\'emplacement',
  'LeadQuality__c': 'Qualité des prospects',
  'TrafficQuality__c': 'Qualité du trafic',
  'QualityOfTheReception__c': 'Qualité de l\'organisation',
  'Observations__c': 'Observations',
  'Improvments__c': 'Axes d\'amélioration',
  'DesiredArea__c': 'Surface souhaitée l\'an prochain',
  'DesiredLocation__c': 'Emplacement souhaité l\'an prochain',
  'Campus__c': 'Campus',
  'Detail__c': 'Type de salon',
  'EndDate': 'Date de fin',
  'StartDate': 'Date de début',
  'Name': 'Nom du salon',
  'DCN__c': 'Candidats n',
  'DCN1__c': 'Candidats n-1',
  'DCN2__c': 'Candidats n-2',
  'PotentielCandidateN__c': 'Candidats potentiels N',
  'PotentialCandidateN1__c': 'Candidats potentiels N-1',
  'PotentialCandidateN2__c': 'Candidats potentiels N-2',
  'ProspectsMetN__c': 'Prospects rencontrés N',
  'ProspectsMetN1__c': 'Prospects rencontrés N-1',
  'ProspectsMetN2__c': 'Prospects rencontrés N-2',
  'RegisteredN__c': 'Inscrits N',
  'RegisteredN1__c': 'Inscrits N-1',
  'RegisteredN2__c': 'Inscrits N-2',
  'City__c': 'Ville du salon',
};

function downloadCSV(salons) {
  const mappedSalons = salons.map((salon) => {
    return Object.keys(propertyMapping).reduce((acc, key) => {
      if (salon[key]) {
        let newKey = propertyMapping[key];
        acc[newKey] = salon[key];
      }
      return acc;
    }, {});
  });
  const csv = Papa.unparse(mappedSalons);
  const csvData = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const csvUrl = URL.createObjectURL(csvData);
  const link = document.createElement('a');
  link.href = csvUrl;
  link.download = 'bilan des salons de l\'annee.csv';
  link.click();
}

// Barre au-dessus du tableau : filtres par statut et recherche rapide.
function SalonsToolbar({ salons, statusFilter, setStatusFilter }) {
  const count = (status) => salons.filter((s) => s.bilanQualiStatus === status).length;
  return (
    <GridToolbarContainer sx={{ p: 1.5, gap: 2, justifyContent: "space-between", flexWrap: "wrap" }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
          Bilan qualitatif :
        </Typography>
        <Chip
          label={`Tous (${salons.length})`}
          color="primary"
          variant={statusFilter === null ? "filled" : "outlined"}
          onClick={() => setStatusFilter(null)}
        />
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <Chip
            key={status}
            label={`${status} (${count(status)})`}
            color={color}
            variant={statusFilter === status ? "filled" : "outlined"}
            onClick={() => setStatusFilter(statusFilter === status ? null : status)}
          />
        ))}
      </Stack>
      <GridToolbarQuickFilter placeholder="Rechercher un salon, une ville…" debounceMs={200} />
    </GridToolbarContainer>
  );
}

function SalonList() {

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);


  const [salons, setSalons] = useState([]);
  const [salonsLoaded, setSalonsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [new_sk, setNew_sk] = useState(null);
  const [userSchools, setUserSchools] = useState([])
  const [loadingSchools, setLoadingSchools] = useState(true)
  const [role, setRole] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    api
      .get("/events/key", { withCredentials: true })
      .then((response) => {
        setNew_sk(response.data.key);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setError(error.message);
      });
  }, []);


useEffect(() => {
    if (new_sk) {
      const userCookie = Cookies.get("user");
      if (userCookie) {
        try {
          const bytes = CryptoJS.AES.decrypt(userCookie, new_sk);
          const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
          const userData = JSON.parse(decryptedData);
          setRole(userData.role);

          if (!userData?.schools )
          {
            console.error("Une erreur est survenue. Veuillez vous déconnecter puis reconnecter")
            return;
          }
          const schoolsArray = userData.schools ? userData.schools.split(';').map(school => school.trim()) : [];
          setUserSchools(schoolsArray);
        } catch (error) {
          console.error("Error:", error);
        } finally {
          setLoadingSchools(false);
        }
      } else {
        setLoadingSchools(false);
      }
    } else {
      setLoadingSchools(true);
    }
  }, [new_sk]);

  useEffect(() => {
    if (new_sk) {
      const token = localStorage.getItem('jwtToken');
      api
        .get("/events/getSalonsList", {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        })
        .then((response) => {
          if (response.data.success) {
            const salonsBilanQuali = response.data.data.map((salon) => {
              const name = salon.Name ? salon.Name.toUpperCase() : '';
              const organizer = salon.Organizer__r?.Name ?? '';
              return {
                ...salon,
                Name: name,
                Organizer: organizer,
                bilanQualiStatus: calculateBilanQualiStatus(salon),
              };
            });
            setSalons(salonsBilanQuali);
          } else {
            console.error("Failed to fetch salons list:", response.data.error);
            setError(response.data.error);
          }
        })
        .catch((err) => {
          console.error("An error occurred:", err);
          setError(err.message);
        })
        .finally(() => setSalonsLoaded(true));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [new_sk]);

  const hasMultipleSchools = userSchools.length > 1;

  const columns = [
    ...((hasMultipleSchools || role ==="Admin") ? [{
      field: "School__c",
      headerName: "École",
      headerClassName: "app-theme",
      width: 90,
    }] : []),
    {
      field: "Name",
      headerName: "Salon",
      headerClassName: "app-theme",
      minWidth: 320,
      flex: 1,
      renderCell: (params) => (
        <MuiLink
          component={Link}
          to={`/FicheSalon/${params.row.Id}`}
          underline="hover"
          fontWeight={500}
          // Le clic sur la ligne navigue déjà : on évite le double traitement.
          onClick={(e) => e.stopPropagation()}
        >
          {params.row.Name}
        </MuiLink>
      ),
    },
    {
      field: "bilanQualiStatus",
      headerName: "Bilan qualitatif",
      headerClassName: "app-theme",
      width: 140,
      renderCell: (params) => params.value && (
        <Chip
          size="small"
          label={params.value}
          color={STATUS_COLORS[params.value] ?? "default"}
          variant={params.value === "Sans Objet" ? "outlined" : "filled"}
        />
      ),
    },
    {
      field: "StartDate",
      headerName: "Début",
      headerClassName: "app-theme",
      width: 105,
      valueFormatter: formatDate,
    },
    {
      field: "EndDate",
      headerName: "Fin",
      headerClassName: "app-theme",
      width: 105,
      valueFormatter: formatDate,
    },
    {
      field: "City__c",
      headerName: "Ville",
      headerClassName: "app-theme",
      width: 120,
    },
    {
      field: "Organizer",
      headerName: "Organisateur",
      headerClassName: "app-theme",
      width: 130,
    },
    {
      field: "Detail__c",
      headerName: "Type",
      headerClassName: "app-theme",
      width: 110,
    },
    {
      field: "Campus__c",
      headerName: "Campus",
      headerClassName: "app-theme",
      width: 100,
    },
    ...QUANTI_GROUPS.flatMap((g) => yearColumns(g.fields)),
  ].map((col) => ({
    ...col,
    // Infobulle avec le nom du salon sur chaque cellule, pour savoir sur
    // quelle ligne on est quand on fait défiler les colonnes de chiffres.
    renderCell: (params) => (
      <Tooltip title={params.row.Name}>
        <span>{col.renderCell ? col.renderCell(params) : params.formattedValue}</span>
      </Tooltip>
    ),
  }));

  const columnGroupingModel = QUANTI_GROUPS.map((g) => ({
    groupId: g.groupId,
    headerAlign: "center",
    headerClassName: "app-theme",
    children: g.fields.map((field) => ({ field })),
  }));

  const rows = statusFilter
    ? salons.filter((s) => s.bilanQualiStatus === statusFilter)
    : salons;

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1">Vos salons</Typography>
          <Typography variant="body2" color="text.secondary">
            Cliquez sur un salon pour voir sa fiche et compléter ses bilans.
          </Typography>
        </Box>
        <Button
          onClick={() => downloadCSV(salons)}
          variant="outlined"
          startIcon={<Download />}
          disabled={salons.length === 0}
        >
          Exporter en CSV
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Impossible de charger les salons : {error}
        </Alert>
      )}

      <Box sx={{ height: "calc(100vh - 240px)", minHeight: 480 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          loading={!error && (loading || loadingSchools || !salonsLoaded)}
          getRowId={(row) => row.Id}
          onRowClick={(params) => navigate(`/FicheSalon/${params.row.Id}`)}
          localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
          disableRowSelectionOnClick
          slots={{
            columnMenu: CustomColumnMenu,
            toolbar: SalonsToolbar,
          }}
          slotProps={{
            toolbar: { salons, statusFilter, setStatusFilter },
          }}
          sx={{
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
          }}
        />
      </Box>
    </Box>
  );
}

export default SalonList;
